import React from 'react'
import { createPortal } from 'react-dom'

import { loadModelViewer } from './loadModelViewer'

//@ts-ignore
import styles from './ProductModelDialog.module.css'

export interface ProductModelStageProps {
  alt: string
  poster: string
  src: string
  /** Replaces the dialog's fixed stage size when the parent sizes the stage. */
  stageClassName?: string
}

// The model-viewer mount, shared by this dialog and the product viewer's 3D tab.
export function ProductModelStage({
  alt,
  poster,
  src,
  stageClassName,
}: ProductModelStageProps) {
  const hostRef = React.useRef<HTMLDivElement>(null)
  const [failed, setFailed] = React.useState(false)
  const [ready, setReady] = React.useState(false)

  React.useEffect(() => {
    const host = hostRef.current
    if (!host) return
    let cancelled = false
    let removeViewerListeners = (): void => undefined
    setFailed(false)
    setReady(false)
    loadModelViewer()
      .then(() => {
        if (cancelled) return
        const reduceMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)',
        ).matches
        const viewer = document.createElement('model-viewer')
        const onLoad = (): void => {
          if (!cancelled) {
            setReady(true)
            setFailed(false)
          }
        }
        const onError = (): void => {
          if (!cancelled) {
            setFailed(true)
            setReady(false)
          }
        }
        viewer.addEventListener('load', onLoad)
        viewer.addEventListener('error', onError)
        removeViewerListeners = () => {
          viewer.removeEventListener('load', onLoad)
          viewer.removeEventListener('error', onError)
        }
        viewer.setAttribute('src', src)
        viewer.setAttribute('alt', alt)
        if (poster) viewer.setAttribute('poster', poster)
        viewer.setAttribute('camera-controls', '')
        // Official models face rear/side at their default orbit.
        if (src.endsWith('/T8030.glb')) {
          viewer.setAttribute('camera-orbit', '180deg 75deg auto')
        } else if (src.endsWith('/T8170_yh.glb')) {
          viewer.setAttribute('camera-orbit', '270deg 75deg auto')
        }
        viewer.style.touchAction = 'none'
        viewer.setAttribute('interaction-prompt', 'auto')
        viewer.setAttribute('shadow-intensity', '1')
        viewer.setAttribute('touch-action', 'none')
        if (!reduceMotion) {
          viewer.setAttribute('auto-rotate', '')
          viewer.setAttribute('auto-rotate-delay', '0')
          viewer.setAttribute('rotation-per-second', '30deg')
        }
        viewer.className = styles['viewer'] ?? ''
        viewer.style.width = '100%'
        viewer.style.height = '100%'
        host.replaceChildren(viewer)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
      removeViewerListeners()
      host.replaceChildren()
    }
  }, [alt, poster, src])

  return (
    <div
      className={`${styles['stage-frame']} ${
        stageClassName ?? styles['stage']
      }`}
      aria-busy={!ready && !failed}
    >
      <div className={styles['viewer-host']} ref={hostRef} />
      {!ready && poster && (
        <img alt="" className={styles['poster']} src={poster} />
      )}
      {(!ready || failed) && (
        <p className={styles['status']} role="status">
          {failed ? 'The 3D view did not load.' : 'Loading 3D…'}
        </p>
      )}
    </div>
  )
}

export interface ProductModelDialogProps {
  alt: string
  onClose: () => void
  poster: string
  src: string
}

export default function ProductModelDialog({
  alt,
  onClose,
  poster,
  src,
}: ProductModelDialogProps) {
  const closeRef = React.useRef<HTMLButtonElement>(null)

  const onCloseRef = React.useRef(onClose)
  onCloseRef.current = onClose

  React.useEffect(() => {
    closeRef.current?.focus()
  }, [])

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [])

  return createPortal(
    <div className={styles['scrim']} onClick={onClose} role="presentation">
      <div
        aria-label={alt}
        aria-modal="true"
        className={styles['sheet']}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <button
          aria-label="Close"
          className={styles['close']}
          onClick={onClose}
          ref={closeRef}
          type="button"
        >
          ×
        </button>
        <ProductModelStage alt={alt} poster={poster} src={src} />
        <p className={styles['caption']}>
          {alt}
          <span className={styles['hint']}>Drag to turn</span>
        </p>
      </div>
    </div>,
    document.body,
  )
}
